import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Route, Redirect, withRouter } from 'react-router-dom';
import { useAuthUser, useAuthSession } from '@nbx/frontend-helpers/subscribe';
import PageTwoColumns from 'components/app/PageTwoColumns';
import { clearError, clearUser, setStep } from 'reduxConfig/auth.creators';
import { useIsMobile } from 'helpers/hooks';
import SEO from 'components/shared/SEO';
import Main from 'components/app/Main';
import store from '../../../store';
import { useRedirectAfterLogin } from './useRedirectAfterLogin';

const microapps = window.MICROFRONTENDS_CONFIG;

/*
  The goal with this wrapper component is to abstract the logic
  for routing the user away if she is already logged in and to
  connect to redux once since the state needed is nearly identical
  to all the auth routes
*/

function useAuthStep() {
  useEffect(() => {
    function setAuthStep(e) {
      store.dispatch(setStep(e.detail || null));
    }

    window.addEventListener('auth_step', setAuthStep);
    return () => window.removeEventListener('auth_step', setAuthStep);
  }, []);
}

export function EntryRoute({ component: Component, auth, dispatch, history, location, ...rest }) {
  const user = useAuthUser();
  const session = useAuthSession();
  const isMobile = useIsMobile();
  useAuthStep();
  useRedirectAfterLogin(user, session, history);

  useEffect(() => {
    if (user === null && auth.user !== null) {
      dispatch(clearUser());
    }
  }, [user, auth, dispatch]);

  // Clear api error on route change
  useEffect(() => {
    const unlisten = history.listen(() => {
      if (auth.error) dispatch(clearError());
    });
    return () => unlisten();
  }, [auth.error, dispatch, history]);

  const renderContents = props => {
    if (
      user &&
      session &&
      !location.pathname.includes(microapps.routes.accountAction?.path) &&
      !location.pathname.includes(microapps.routes.mobileSessionExpired?.path)
    ) {
      return (
        <Redirect to={{ pathname: microapps.routes.dashboards.path, state: { from: props.location } }} />
      );
    }
    return (
      <PageTwoColumns>
        <Component dispatch={dispatch} isMobile={isMobile} {...auth} {...props} />
      </PageTwoColumns>
    );
  };

  // prettier-ignore
  return (
    <Main>
      <SEO />
      <Route {...rest} render={renderContents} />
    </Main>
  );
}

EntryRoute.propTypes = {
  component: PropTypes.func.isRequired,
  auth: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired
};

function mapStateToProps(state) {
  return { auth: state };
}

export default connect(mapStateToProps)(withRouter(EntryRoute));
